import { createRef, useCallback, useEffect, useRef } from 'react';
import throttle from 'lodash/throttle';

import { H4 } from '@r1-frontend/ui-react/components/typography/heading';

import { CDN_API_ENDPOINT } from '~/src/constants';

import * as ST from './styled';
import * as STA from './styled-animation';

const START_ANIMATION_COEFF = 40; // при коэф. равном нулю, анимация начнется в серендине экрана, если больше то позже.
const ANIMATION_STEPS = 5;

const imgagesParams = [
    {
        width: '21%',
        height: '21%',
        top: '45%',
        left: '35%',
        rotateDegree: '14deg',
        src: require('~/static/images/internet/represent/annotation/rectangle-0.png'),
    },
    {
        top: '0%',
        right: '20%',
        rotateDegree: '-12deg',
        src: require('~/static/images/internet/represent/annotation/rectangle-1.png'),
    },
    {
        top: '37%',
        right: '0%',
        rotateDegree: '-10deg',
        src: require('~/static/images/internet/represent/annotation/rectangle-2.png'),
    },
    {
        width: '19%',
        height: '19%',
        left: '62%',
        bottom: '10%',
        rotateDegree: '22deg',
        src: require('~/static/images/internet/represent/annotation/rectangle-3.png'),
    },
    {
        width: '19%',
        height: '19%',
        left: '5%',
        bottom: '0%',
        rotateDegree: '-21deg',
        src: require('~/static/images/internet/represent/annotation/rectangle-4.png'),
    },
    {
        left: '0%',
        top: '29%',
        rotateDegree: '7deg',
        src: require('~/static/images/internet/represent/annotation/rectangle-5.png'),
    },
];

const targetPositions = [
    [100, -100],
    [-20, 110],
    [-110, -60],
    [-20, -240],
    [270, -280],
    [240, -20],
];

const PATH = `${CDN_API_ENDPOINT}/static/images/internet/represent/annotation`;

interface IAnnotation {
    isCountryHouse: boolean,
}

export const Annotation = ({ isCountryHouse }: IAnnotation): JSX.Element => {
    const annotationRef = useRef<HTMLDivElement>(null);
    const imgRefs = useRef<React.RefObject<HTMLImageElement>[]>(targetPositions.map(() => createRef()));
    const scrollPosition = useRef<number>(0);

    const onScroll = useCallback(throttle(() => {
        const annotationTop = annotationRef.current?.offsetTop || 0;
        const annotationHeight = annotationRef.current?.offsetHeight || 0;
        const stepSize = annotationHeight / ANIMATION_STEPS;

        scrollPosition.current = window.pageYOffset;

        const viewHeight = document.documentElement.clientHeight;
        const documentCenter = (viewHeight / 2) - (viewHeight / 200 * START_ANIMATION_COEFF);
        const currByCenter = documentCenter + scrollPosition.current;
        const annotationToCenter = currByCenter - annotationTop;
        const currAnimStep = Math.round(annotationToCenter / stepSize);

        if (currAnimStep > 0 && currAnimStep <= ANIMATION_STEPS) {
            imgRefs.current.forEach((item, index) => {
                const elem = item.current;
                const [_x, _y] = targetPositions[index];
                const xCoord = _x / ANIMATION_STEPS * currAnimStep;
                const yCoord = _y / ANIMATION_STEPS * currAnimStep;

                if (elem) {
                    elem.style.transform = `translate(${xCoord}%, ${yCoord}%)`;
                }
            });
        } else {
            imgRefs.current.forEach((item, index) => {
                const elem = item.current;
                const [xCoord, yCoord] = targetPositions[index];

                if (elem) {
                    elem.style.transform = currAnimStep < 1 ? 'translate(0%, 0%)' : `translate(${xCoord}%, ${yCoord}%)`;
                }
            });
        }
    }, 200), []);

    useEffect(() => {
        scrollPosition.current = window.pageYOffset;

        window.addEventListener('scroll', onScroll);

        return () => window.removeEventListener('scroll', onScroll);
    }, [onScroll]);

    return (
        <ST.Annotation ref={annotationRef}>
            <ST.Content>
                <ST.TextContent>
                    <H4 as={'span'}>
                        {isCountryHouse
                            ? <>Созванивайтесь по видео с друзьями, смотрите фильмы онлайн и заказывайте пиццу в час ночи с&nbsp;помощью современных онлайн-возможностей</>
                            : 'Наши тарифы — это выгодные комплекты, в которые входят интернет, телеканалы и подписки. Всё вместе выходит гораздо дешевле, чем по отдельности.'
                        }
                    </H4>
                </ST.TextContent>
                <ST.ImgContent>
                    <STA.AnimationWrap>
                        {imgagesParams.map((item, index) => {
                            return (
                                <STA.VideoWrap
                                    key={index}
                                    ref={imgRefs.current[index]}
                                    imgParams={item}
                                >
                                    <video
                                        playsInline
                                        muted
                                        loop
                                        autoPlay
                                        src={`${PATH}/video-${index}.mp4`}
                                        poster={item.src}
                                    />
                                </STA.VideoWrap>
                            );
                        })}
                    </STA.AnimationWrap>
                </ST.ImgContent>
            </ST.Content>
        </ST.Annotation>
    );
};
